import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { i18n } from '@international/mastodon-i18n';

import { LegacyPersonalProfileBanner } from 'components/CheckrPersonalProfileBanner';
import ReviewCTABannerContainer from 'components/ReviewCTABanner';
import candidateFacingStatus from 'components/Reports/Report/candidateFacingStatus';
import useHarnessFlag from 'api/harness/useHarnessFlag';
import { HARNESS_FLAGS } from 'api/harness/harnessFlags';
import StatusBadgeInfoContext from 'components/StatusBadge/StatusBadgeInfoContext';
import { MastodonReportProvider } from 'components/Reports/Report/MastodonReport/MastodonReportContext';
import { PATHS, PERSONAL_URL } from '../../constants';
import { trackAnalyticsEvent } from '../../actions';
import { useAddCustomIncident } from '../../providers/CustomIncidentProvider';
import { generateCustomIncidentsContentKey } from '../Reports/Report/lib/incidents';
import MainContainer from '../MainContainer';
import { Navigation } from './Navigation';
import ReportContainer from '../Reports/Report/Report';
import { Report as ReportInterface } from '../../types/report';
import { ANALYTICS_EVENTS } from '../../lib/analytics';
import ReportContext, { ReportPageReport } from './ReportContext';

import './ReportPage.scss';

const COUNTY_DELAY_MESSAGE_COUNTIES = [{ county: 'los angeles', state: 'ca' }];

interface ReportPageProps {
  report: ReportPageReport;
  isLoading: boolean;
  international: boolean;
  contactCompanyForReport?: boolean;
}

export const ReportPage: React.FC<ReportPageProps> = ({
  report,
  isLoading,
  international,
  contactCompanyForReport = false,
}) => {
  // Per-candidate report status override, targeted by candidate email.
  // Rendering waits for isResolved (ready, cached, or timed out) so targeted
  // candidates never flash the unmapped consider/escalated/review state
  // while the Split SDK initializes (readyTimeout caps the wait at 10s).
  const {
    enabled: candidateFacingStatusEnabled,
    isResolved: candidateFacingStatusResolved,
  } = useHarnessFlag({
    flagKey: HARNESS_FLAGS.CANDIDATE_FACING_STATUS,
    splitKey: report?.candidate?.email,
  });

  // Report items render as Mastodon components when this is on. Account
  // traffic type, so it is keyed by the report's account id (the server render
  // uses the same key, which keeps the report and its PDF in step). Without an
  // account id there is nothing to target, so it stays off. Rendering waits for
  // it to resolve so the report never switches components after it shows.
  const mastodonReportAccountId = report?.account?.id;
  const { enabled: mastodonReportFlag, isResolved: mastodonReportResolved } =
    useHarnessFlag({
      flagKey: HARNESS_FLAGS.MASTODON_REPORT,
      splitKey: mastodonReportAccountId,
    });
  const mastodonReportEnabled =
    Boolean(mastodonReportAccountId) && mastodonReportFlag;

  const dispatch = useDispatch();
  const addCustomIncident = useAddCustomIncident();
  const [searchParams] = useSearchParams();
  const returnToPersonal = searchParams.get('source') === 'personal';
  const reportId = report.id;
  const reportStatus = report.displayStatus;
  const [sentAnalytics, setSentAnalytics] = useState(false);
  // The contactCompanyForReport path renders without waiting on the flag;
  // everything else waits for the flag to resolve (see the render gate).
  const pageVisible =
    !isLoading &&
    (contactCompanyForReport ||
      (candidateFacingStatusResolved && mastodonReportResolved));
  useEffect(() => {
    // Track only once the report UI actually renders, not during the blank
    // frame while the candidate-facing status flag resolves.
    if (pageVisible && report && !sentAnalytics) {
      setSentAnalytics(true);
      dispatch(trackAnalyticsEvent(ANALYTICS_EVENTS.REPORT_VIEWED));
    }
  }, [pageVisible, dispatch, report, sentAnalytics]);
  useEffect(() => {
    const customIncidentContextChecks = generateCustomIncidentsContentKey(
      report as ReportInterface,
    );

    const customIncidentContentKeys = customIncidentContextChecks
      .filter(({ context: reportContext }) => {
        return COUNTY_DELAY_MESSAGE_COUNTIES.some(
          ({ county, state }: { county: string; state: string }) => {
            return (
              county.toLowerCase() ===
                (reportContext as { county: string }).county &&
              state.toLowerCase() === (reportContext as { state: string }).state
            );
          },
        );
      })
      .map(({ contentKey }) => contentKey);

    if (reportId && customIncidentContentKeys.length > 0) {
      addCustomIncident(customIncidentContentKeys);
    }
  }, [addCustomIncident, report, reportId]);

  const displayStatus = candidateFacingStatus(
    report.displayStatus,
    candidateFacingStatusEnabled,
  );

  const homeLinkText = i18n.getStr('components.Report.homeButton');

  const homeLink = useMemo(() => {
    let href = international
      ? PATHS.INTERNATIONAL_REPORTS_OVERVIEW
      : PATHS.REPORTS_OVERVIEW;

    if (returnToPersonal) {
      href = `${PERSONAL_URL}/orders`;
    }

    return {
      href,
      text: homeLinkText,
    };
  }, [international, returnToPersonal, homeLinkText]);

  if (!pageVisible) return null;

  return (
    <ReportContext.Provider value={report}>
      <div>
        <ReviewCTABannerContainer />
        <LegacyPersonalProfileBanner
          reportId={reportId}
          reportStatus={reportStatus}
        />
        <MainContainer
          className={'report__wrapper report__wrapper--new-flow'}
          header={<Navigation homeLink={homeLink} />}
        >
          {report?.account?.name && (
            <div
              data-testid='report-title'
              className={'report__header report__header--status'}
            >
              <h1 className={`report--account-name`}>
                {report?.brandingName || report?.account?.name}
              </h1>
              {report.displayStatus ? (
                <p className='report--status' data-testid='report-title-status'>
                  <b>{i18n.getStr('components.Report.Header.status.title')}:</b>{' '}
                  {i18n.getStr(
                    `components.Report.Header.status.${displayStatus}`,
                  )}
                </p>
              ) : null}
            </div>
          )}
          {contactCompanyForReport ? (
            <div>
              <h2>
                To access your report please contact{' '}
                <a
                  href='https://help.checkr.com/s/contactsupport'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Checkr&apos;s Help Center
                </a>
                .
              </h2>
            </div>
          ) : (
            report && (
              <StatusBadgeInfoContext.Provider value>
                <MastodonReportProvider enabled={mastodonReportEnabled}>
                  <ReportContainer
                    report={report}
                    international={international}
                    candidateFacingStatusEnabled={candidateFacingStatusEnabled}
                  />
                </MastodonReportProvider>
              </StatusBadgeInfoContext.Provider>
            )
          )}
        </MainContainer>
      </div>
    </ReportContext.Provider>
  );
};
